import { useState, useEffect } from 'react'
import { Save, RotateCcw, Settings, Terminal, Globe, LayoutDashboard, ChevronDown, ChevronRight, Plus, Pencil, Trash2, Tag, GripVertical } from 'lucide-react'
import { listPlatformSettings, updatePlatformSettings, resetPlatformSettings, listCategories, createCategory, updateCategory, deleteCategory, type Category } from '../../lib/api'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { AVAILABLE_COLORS, getCategoryColorClasses } from '../../lib/categoryColors'

interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'number' | 'boolean' | 'select'
  options?: { value: string; label: string }[]
  placeholder?: string
  description?: string
}

interface CategoryConfig {
  key: string
  label: string
  icon: React.ElementType
  description: string
  fields: FieldConfig[]
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'terminal',
    label: 'Terminal',
    icon: Terminal,
    description: 'Default settings for workspace terminal',
    fields: [
      { key: 'shell', label: 'Shell', type: 'select', options: [
        { value: 'bash', label: 'Bash' },
        { value: 'zsh', label: 'Zsh' },
        { value: 'fish', label: 'Fish' },
      ]},
      { key: 'font_size', label: 'Font Size', type: 'number', placeholder: '14' },
      { key: 'font_family', label: 'Font Family', type: 'text', placeholder: 'monospace' },
      { key: 'theme', label: 'Theme', type: 'select', options: [
        { value: 'dark', label: 'Dark' },
        { value: 'light', label: 'Light' },
      ]},
      { key: 'scrollback_lines', label: 'Scrollback Lines', type: 'number', placeholder: '10000' },
      { key: 'cursor_style', label: 'Cursor Style', type: 'select', options: [
        { value: 'block', label: 'Block' },
        { value: 'underline', label: 'Underline' },
        { value: 'bar', label: 'Bar' },
      ]},
    ],
  },
  {
    key: 'browser',
    label: 'Browser',
    icon: Globe,
    description: 'Default settings for browser side panel',
    fields: [
      { key: 'homepage', label: 'Homepage', type: 'text', placeholder: 'https://google.com' },
      { key: 'dark_mode', label: 'Force Dark Mode', type: 'boolean' },
      { key: 'zoom_level', label: 'Zoom Level (%)', type: 'number', placeholder: '100' },
    ],
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Default settings for the dashboard UI',
    fields: [
      { key: 'theme', label: 'Theme', type: 'select', options: [
        { value: 'system', label: 'System' },
        { value: 'dark', label: 'Dark' },
        { value: 'light', label: 'Light' },
      ]},
      { key: 'show_recent_apps', label: 'Show Recent Apps', type: 'boolean' },
    ],
  },
]

export default function AdminPlatformSettings() {
  const [settings, setSettings] = useState<Record<string, Record<string, unknown>>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['terminal', 'browser']))

  // App Categories management
  const [appCategories, setAppCategories] = useState<Category[]>([])
  const [catLoading, setCatLoading] = useState(true)
  const [editingCat, setEditingCat] = useState<Category | null>(null)
  const [newCat, setNewCat] = useState<{ slug: string; name: string; color: string } | null>(null)
  const [catError, setCatError] = useState<string | null>(null)

  useEffect(() => {
    loadSettings()
    loadCategories()
  }, [])

  async function loadCategories() {
    try {
      const cats = await listCategories()
      setAppCategories(cats)
    } catch {
      // ignore
    } finally {
      setCatLoading(false)
    }
  }

  async function handleCreateCategory() {
    if (!newCat || !newCat.slug || !newCat.name) return
    setCatError(null)
    try {
      await createCategory({ slug: newCat.slug, name: newCat.name, color: newCat.color, display_order: appCategories.length + 1 })
      setNewCat(null)
      await loadCategories()
    } catch (err) {
      setCatError(err instanceof Error ? err.message : 'Failed to create category')
    }
  }

  async function handleUpdateCategory(slug: string, data: { name?: string; color?: string }) {
    setCatError(null)
    try {
      await updateCategory(slug, data)
      setEditingCat(null)
      await loadCategories()
    } catch (err) {
      setCatError(err instanceof Error ? err.message : 'Failed to update category')
    }
  }

  async function handleDeleteCategory(slug: string) {
    setCatError(null)
    try {
      await deleteCategory(slug)
      await loadCategories()
    } catch (err) {
      setCatError(err instanceof Error ? err.message : 'Failed to delete category')
    }
  }

  async function loadSettings() {
    try {
      const data = await listPlatformSettings()
      const settingsMap: Record<string, Record<string, unknown>> = {}
      data.forEach(s => {
        // Only load UI-related settings here
        if (['terminal', 'browser', 'dashboard'].includes(s.category)) {
          settingsMap[s.category] = s.settings as Record<string, unknown>
        }
      })
      setSettings(settingsMap)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  function getValue(category: string, key: string): unknown {
    return settings[category]?.[key]
  }

  function setValue(category: string, key: string, value: unknown) {
    setSettings(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: value }
    }))
  }

  async function handleSave(category: string) {
    setSaving(category)
    setError(null)
    setSuccess(null)

    try {
      await updatePlatformSettings(category, settings[category] || {})
      setSuccess(`${category} settings saved successfully`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(null)
    }
  }

  async function handleReset(category: string) {
    setSaving(category)
    setError(null)

    try {
      const result = await resetPlatformSettings(category)
      setSettings(prev => ({ ...prev, [category]: result.settings as Record<string, unknown> }))
      setSuccess(`${category} settings reset to defaults`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings')
    } finally {
      setSaving(null)
    }
  }

  function toggleCategory(category: string) {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  function renderField(category: string, field: FieldConfig) {
    const value = getValue(category, field.key)

    return (
      <div key={field.key} className="space-y-1">
        <label className="block text-sm font-medium">{field.label}</label>
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
        {field.type === 'text' && (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => setValue(category, field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        )}
        {field.type === 'number' && (
          <input
            type="number"
            value={(value as number) ?? ''}
            onChange={(e) => setValue(category, field.key, e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        )}
        {field.type === 'boolean' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value as boolean ?? false}
              onChange={(e) => setValue(category, field.key, e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground">Enabled</span>
          </label>
        )}
        {field.type === 'select' && field.options && (
          <select
            value={(value as string) || field.options[0]?.value || ''}
            onChange={(e) => setValue(category, field.key, e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Platform Settings</h1>
          <p className="text-muted-foreground">Configure default UI settings</p>
        </div>
        <Settings className="w-8 h-8 text-muted-foreground" />
      </div>

      {error && (
            <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-lg bg-green-500/10 text-green-500 border border-green-500/20">
              {success}
            </div>
          )}

          <div className="space-y-4">
            {CATEGORIES.map((category) => {
              const Icon = category.icon
              const isExpanded = expandedCategories.has(category.key)

              return (
                <div key={category.key} className="border border-border rounded-xl bg-card overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category.key)}
                    className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-primary" />
                      <div className="text-left">
                        <h3 className="font-semibold">{category.label}</h3>
                        <p className="text-sm text-muted-foreground">{category.description}</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>

                  {isExpanded && (
                    <div className="p-4 pt-0 border-t border-border">
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 pt-4">
                        {category.fields.map((field) => renderField(category.key, field))}
                      </div>

                      <div className="flex gap-3 mt-6 pt-4 border-t border-border">
                        <ConfirmDialog
                          trigger={
                            <button
                              disabled={saving === category.key}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                            >
                              <RotateCcw className="w-4 h-4" />
                              Reset to Defaults
                            </button>
                          }
                          title="Reset Settings"
                          description={`Reset ${category.label} settings to platform defaults? This cannot be undone.`}
                          confirmLabel="Reset"
                          variant="destructive"
                          onConfirm={() => handleReset(category.key)}
                        />
                        <button
                          onClick={() => handleSave(category.key)}
                          disabled={saving === category.key}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" />
                          {saving === category.key ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* App Categories Section */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Tag className="w-5 h-5 text-primary" />
                <div>
                  <h2 className="text-lg font-semibold">App Categories</h2>
                  <p className="text-sm text-muted-foreground">Manage categories for Browse page filtering</p>
                </div>
              </div>
              <button
                onClick={() => setNewCat({ slug: '', name: '', color: 'blue' })}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>

            {catError && (
              <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 text-sm">
                {catError}
              </div>
            )}

            {/* Add new category form */}
            {newCat && (
              <div className="mb-4 p-4 border border-primary/30 rounded-xl bg-card">
                <h3 className="text-sm font-medium mb-3">New Category</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Slug</label>
                    <input
                      type="text"
                      value={newCat.slug}
                      onChange={(e) => setNewCat({ ...newCat, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="my-category"
                      className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Name</label>
                    <input
                      type="text"
                      value={newCat.name}
                      onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                      placeholder="My Category"
                      className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Color</label>
                    <select
                      value={newCat.color}
                      onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {AVAILABLE_COLORS.map(c => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleCreateCategory}
                    disabled={!newCat.slug || !newCat.name}
                    className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm disabled:opacity-50"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => setNewCat(null)}
                    className="px-3 py-1.5 border border-border rounded-lg hover:bg-secondary text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Category list */}
            {catLoading ? (
              <div className="text-center py-8 text-muted-foreground text-sm">Loading categories...</div>
            ) : appCategories.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
                No categories defined yet. Add one to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {appCategories.map((cat) => {
                  const colorClasses = getCategoryColorClasses(cat.color)
                  return (
                    <div key={cat.id} className="flex items-center gap-3 p-3 border border-border rounded-lg bg-card">
                      <GripVertical className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                      <span className={`w-3 h-3 rounded-full ${colorClasses.dot} flex-shrink-0`} />

                      {editingCat?.id === cat.id ? (
                        <>
                          <input
                            type="text"
                            value={editingCat.name}
                            onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                            className="flex-1 px-2 py-1 rounded border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <select
                            value={editingCat.color}
                            onChange={(e) => setEditingCat({ ...editingCat, color: e.target.value })}
                            className="px-2 py-1 rounded border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {AVAILABLE_COLORS.map(c => (
                              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleUpdateCategory(cat.slug, { name: editingCat.name, color: editingCat.color })}
                            className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCat(null)}
                            className="px-2 py-1 border border-border rounded text-xs hover:bg-secondary"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium">{cat.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{cat.slug}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${colorClasses.badge}`}>
                            {cat.color}
                          </span>
                          <button
                            onClick={() => setEditingCat({ ...cat })}
                            className="p-1 rounded hover:bg-secondary text-muted-foreground"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <ConfirmDialog
                            trigger={
                              <button
                                className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            }
                            title="Delete Category"
                            description={`Delete "${cat.name}"? Apps using this category will become uncategorized.`}
                            confirmLabel="Delete"
                            variant="destructive"
                            onConfirm={() => handleDeleteCategory(cat.slug)}
                          />
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
    </div>
  )
}
